import { useDispatch, useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import { useSelectRealUser } from "@shared/utils/hooks/misc";
import { updateStoreUserIsOnboarding } from "@shared/features/onboarding/utils";
import { selectWorkspace } from "@shared/slices/common.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { useHistory } from "react-router-dom";
import React, { CSSProperties, useContext, useState } from "react";
import styled from "styled-components";
import { DcaBotCreateeditOriginValueType } from "analytics/events";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import useSaveBotButton from "@shared/features/bots/BotForm/useSaveBotButton";
import { displayResultMessage, openMessage } from "utils/misc";
import {
  BOT_TYPE_ANALYTICS_MAPPING,
  BOT_TYPE_TO_BASE_PATH,
  NEW_BOT_ID_QUERY_PARAM,
} from "features/bots/common/constants";
import { ButtonSize } from "antd/es/button";
import ConfirmAutoTradingModal from "features/onboarding/common/ConfirmAutoTradingModal";
import ConfirmBulkEditModal from "features/bots/BotForm/common/ConfirmBulkEditModal";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { ADMIN_TRADING_CONFIGURATION_PATH } from "common/constants";
import ConfirmBotModal from "features/bots/BotForm/common/ConfirmBotModal";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import DebouncedButton from "common/Buttons/DebouncedButton";
import {
  BotData,
  BotStatus,
  BotType,
  WorkspaceType,
  LanguageNameSpaces,
} from "@shared/types";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { BOT_FORM_SAVE_BUTTON_ID } from "testIds.ts";
import {
  getBotMethod,
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import useTrackSaveBlocked from "features/bots/common/useTrackSaveBlocked";

interface SaveBotButtonProps {
  actionLabel?: string;
  botId?: number;
  disabled?: boolean;
  style?: CSSProperties;
  analyticsBotOriginType: DcaBotCreateeditOriginValueType;
  errorMessage?: string;
  copiedFrom?: number;
  buttonSize?: ButtonSize;
  onSuccess?: (botData?: BotData) => void;
  onSaveClick?: () => void;
  onDisabledClick?: () => void;
  /** Defaults to the bot form's save button id. */
  testId?: string;
  /** Disabled, but its click still does something useful. */
  showAsEnabled?: boolean;
  /** For the blocked-save event when no message is shown. */
  blockedReason?: string;
}

const SaveBotButton = ({
  actionLabel,
  botId,
  disabled,
  style,
  analyticsBotOriginType,
  errorMessage,
  copiedFrom,
  buttonSize,
  onSuccess: propsOnSuccess,
  onSaveClick: propsOnSaveClick,
  onDisabledClick: propsOnDisabledClick,
  testId,
  showAsEnabled,
  blockedReason,
}: SaveBotButtonProps) => {
  const { botType, isChannelConfiguration, subGroup } =
    useContext(BotFormContext)!;
  const history = useHistory();
  const { getUpdatedPath } = useHistoryWithQuery();
  const { analytics } = useContext(AnalyticsContext);
  const trackOnboarding = useOnboardingAnalytics();
  // A click the form refuses (no account, invalid values): what it said.
  const trackSaveBlocked = useTrackSaveBlocked();
  const onDisabledClick = () => {
    trackSaveBlocked(errorMessage ?? blockedReason);
    propsOnDisabledClick?.();
  };
  const trackSaveClick = () =>
    analytics?.botCreateSaveChangesClicked({
      dcaBotCreateeditOrigin: analyticsBotOriginType,
      botType: BOT_TYPE_ANALYTICS_MAPPING[botType],
    });

  const [warningModalContent, setWarningModalContent] = useState<string[]>();
  const localOpenAmountWarningConfirmModal = (
    amountWarningTexts: string[],
    openConfirmModal: () => void
  ) => {
    setWarningModalContent(amountWarningTexts);
    openConfirmModal();
  };

  const getQueryToAppend = (botData?: BotData) => {
    if (
      botData &&
      !isChannelConfiguration &&
      botType === BotType.SIGNAL &&
      botData.status === BotStatus.ACTIVE &&
      !subGroup?.is_personal
    ) {
      return { [NEW_BOT_ID_QUERY_PARAM]: botData.id.toString() };
    }
  };

  const onSuccess = (botData?: BotData) => {
    const nextPath = isChannelConfiguration
      ? ADMIN_TRADING_CONFIGURATION_PATH
      : BOT_TYPE_TO_BASE_PATH[botType];

    const updatedPath = getUpdatedPath(
      getQueryToAppend(botData),
      undefined,
      nextPath
    );
    history.push(updatedPath);
  };

  const onboardingSetup = useContext(OnboardingBotSetupContext);
  const onboarding = useContext(OnboardingContext);
  const workspace = useSelector(selectWorkspace);
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  // Onboarding's demo DCA and Grid bots say what they create; like every
  // bot, they're created through the confirmation dialog.
  const demoOnboardingBot =
    !!onboardingSetup &&
    workspace === WorkspaceType.DEMO &&
    !botId &&
    (botType === BotType.GRID || botType === BotType.ASAP);

  const onSaveChangesSuccess =
    propsOnSuccess ?? onboardingSetup?.onCreated ?? onSuccess;

  const {
    isLoading,
    onSaveChangesClick,
    saveChanges,
    actionButtonDisplay,
    botData,
    confirmModalData,
    confirmBulkEditModalData,
    gridBotValuesChanged,
    botsQuotaModal,
  } = useSaveBotButton({
    botId,
    disabled,
    errorMessage,
    copiedFrom,
    displayResultMessage,
    trackSaveClick,
    onSuccess: onSaveChangesSuccess,
    openMessage,
    openAmountWarningConfirmModal: (
      amountWarningTexts: string[],
      openConfirmModal: () => void
    ) => {
      localOpenAmountWarningConfirmModal(amountWarningTexts, openConfirmModal);
    },
    onSaveClick: propsOnSaveClick,
    onDisabledClick,
    useQuotaModal,
  });

  // The confirmation is its own onboarding step: shown, cancelled (in the
  // modal), or saved; the saved bot is recorded in demo too, where the
  // backend doesn't send its bot-created event.
  useOnboardingStepView(
    onboarding && !botId && confirmModalData.isOpen ? "bot_confirm" : undefined
  );
  // A user still in onboarding who creates a bot in the platform (they
  // came from a channel post's button) has finished it with that bot.
  const realUser = useSelectRealUser();
  const dispatch = useDispatch();
  const [finishOnboarding] =
    sharedLib.sharedApi.authentication.useFinishOnboardingMutation();
  const finishOnboardingWithBot = () => {
    if (onboarding || botId || isChannelConfiguration) return;
    if (!realUser?.is_onboarding) return;
    finishOnboarding()
      .unwrap()
      .then(() => dispatch(updateStoreUserIsOnboarding(false)))
      .catch(() => undefined);
  };
  const saveWithAnalytics = async (
    ...args: Parameters<typeof saveChanges>
  ): ReturnType<typeof saveChanges> => {
    try {
      const saved = await saveChanges(...args);
      finishOnboardingWithBot();
      if (onboarding && !botId)
        trackOnboarding("Bot Created", {
          bot_type: getBotMethod(botType),
          trading_mode: workspace === WorkspaceType.DEMO ? "demo" : "live",
        });
      return saved;
    } catch (error) {
      if (onboarding)
        trackOnboarding("Step Failed", {
          step: "bot_confirm",
          // A quota rejection has no error; a paid plan answers 402.
          error_code:
            error === undefined
              ? "bot_quota_exceeded"
              : (error as { status?: number })?.status === 402
              ? "subscription_required"
              : "bot_creation_failed",
        });
      throw error;
    }
  };

  return (
    <>
      <StyledButton
        data-testid={testId ?? BOT_FORM_SAVE_BUTTON_ID}
        aria-disabled={disabled && !showAsEnabled ? "true" : "false"}
        type="primary"
        onClick={onSaveChangesClick}
        style={style}
        loading={isLoading && !confirmModalData.isOpen}
        $disabled={disabled && !showAsEnabled}
        size={buttonSize || "middle"}
      >
        {actionLabel ??
          (demoOnboardingBot
            ? t("revision.create_demo_bot")
            : actionButtonDisplay)}
      </StyledButton>
      <ConfirmBotModal
        actionLabel={actionLabel}
        closeModal={confirmModalData.close}
        saveChanges={saveWithAnalytics}
        isOpen={confirmModalData.isOpen && !warningModalContent}
        botData={botData}
        isLoading={isLoading}
        onSuccess={onSaveChangesSuccess}
        gridBotValuesChanged={gridBotValuesChanged}
      />
      <ConfirmAutoTradingModal
        isVisible={confirmModalData.isOpen && !!warningModalContent}
        content={warningModalContent}
        onOk={() => setWarningModalContent(undefined)}
        onCancel={() => {
          setWarningModalContent(undefined);
          confirmModalData.close();
        }}
      />
      <ConfirmBulkEditModal
        isVisible={confirmBulkEditModalData.isOpen}
        title={confirmBulkEditModalData.title}
        content={confirmBulkEditModalData.content}
        onOk={confirmBulkEditModalData.onOk}
        onCancel={confirmBulkEditModalData.onClose}
        numValidBots={confirmBulkEditModalData.numValidBots}
        numInvalidBots={confirmBulkEditModalData.numInvalidBots}
        buttonLoading={isLoading}
      />
      {botsQuotaModal}
    </>
  );
};

export default SaveBotButton;

const StyledButton = styled(DebouncedButton)<{ $disabled?: boolean }>`
  width: 100%;
  ${(p) =>
    p.$disabled &&
    `
    opacity: 0.5;
  `}
`;
